import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import {
  Modal, Button, DatePicker, Input, Select,
} from "antd";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import moment from "moment";
import { updatedReportDelay, getReasons } from "../../../actions/reportDelay";
import ReportDelayTable from "./ReportDelayTable";
import { getUser } from "../../../utils/helpers";
import "./UpdateDelayReportModal.css";

export default function UpdateDelayReportModal({
  open,
  onCancel,
  selectedRows,
}) {
  const [expectedDate, setExpectedDate] = useState(null);
  const [delayReasonType, setDelayReasonType] = useState("");
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [delayReasons, setDelayReasons] = useState([]);
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const abortControllerRef = useRef();

  useEffect(() => {
    const controller = new AbortController();

    if (open) {
      setExpectedDate(null);
      setDelayReasonType("");
      setComment("");
      setDelayReasons([]);

      const fetchReasons = async () => {
        setReasonsLoading(true);
        const res = await getReasons(controller.signal);
        if (!controller.signal.aborted) {
          if (res.success) {
            setDelayReasons(res.data ?? []);
          } else if (!res.canceled) {
            toast.error(res.message || "Failed to load delay reasons");
          }
          setReasonsLoading(false);
        }
      };

      fetchReasons();
    }

    return () => { controller.abort(); };
  }, [open]);

  const isCommentRequired = useMemo(
    () => delayReasonType === "Other",
    [delayReasonType],
  );

  const groupedReasonOptions = useMemo(() => {
    const standard = delayReasons.filter((r) => r.reasons_category === "standard");
    const labOps = delayReasons.filter((r) => r.reasons_category === "labops");
    const groups = [];
    if (standard.length > 0) {
      groups.push({
        label: "Standard Reasons",
        options: standard.map((r) => ({ value: r.name, label: r.name })),
      });
    }
    if (labOps.length > 0) {
      groups.push({
        label: "Lab Ops Reasons",
        options: labOps.map((r) => ({ value: r.name, label: r.name })),
      });
    }
    return groups;
  }, [delayReasons]);

  const canSubmit = useMemo(
    () => Boolean(selectedRows?.length)
      && Boolean(expectedDate)
      && Boolean(delayReasonType)
      && (!isCommentRequired || comment.trim().length > 0)
      && !reasonsLoading
      && delayReasons.length > 0,
    [selectedRows, expectedDate, delayReasonType, comment, isCommentRequired, reasonsLoading, delayReasons],
  );

  const columns = useMemo(() => ([
    {
      title: "WO ID",
      dataIndex: "workorder_id",
      key: "workorder_id",
      onCell: () => ({ style: { minWidth: 100 } }),
    },
    {
      title: "Genista ID",
      dataIndex: "genista_id",
      key: "genista_id",
      onCell: () => ({ style: { minWidth: 100 } }),
    },
    {
      title: "Test",
      dataIndex: "test_name",
      key: "test_name",
      onCell: () => ({ style: { minWidth: 100 } }),
    },
    {
      title: "Test Category",
      dataIndex: "category_name",
      key: "category_name",
      onCell: () => ({ style: { minWidth: 120 } }),
    },
    {
      title: "Result Due By",
      dataIndex: "current_eta",
      key: "current_eta",
      onCell: () => ({ style: { minWidth: 130 } }),
      render: (value) => (value ? moment(value).format("MM/DD/YYYY") : "-"),
    },
  ]), []);

  const handleCancel = () => {
    onCancel();
  };

  const postETAOverride = useCallback(async (payload) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    const response = await updatedReportDelay(payload, controller.signal);
    if (response.success) {
      toast.success(response.message);
      setLoading(false);
      onCancel(true); // TRUE if update successfull
    } else if (!response.canceled) {
      toast.error(response.message);
      setLoading(false);
    }
  }, [onCancel]);

  const handleSubmit = useCallback(() => {
    if (!selectedRows || selectedRows?.length === 0) {
      toast.error("No row selected");
      return;
    }
    if (!expectedDate) {
      toast.error("Select expected date");
      return;
    }
    if (!delayReasonType) {
      toast.error("Select delay reason type");
      return;
    }
    const trimmedComment = String(comment || "").trim();
    if (isCommentRequired && !trimmedComment) {
      toast.error("Enter comment");
      return;
    }

    const user = getUser();
    const payload = selectedRows.map((record) => {
      const current_eta = dayjs(record.current_eta).format("YYYY-MM-DD");
      const expected_eta = dayjs(expectedDate).format("YYYY-MM-DD");
      return {
        tests_in_sample_PK: record.tests_in_sample_pk,
        reason: delayReasonType,
        comments: trimmedComment,
        current_eta,
        expected_eta,
        user_id: user.id,
      };
    });

    postETAOverride(payload);
  }, [selectedRows, expectedDate, delayReasonType, comment, isCommentRequired, postETAOverride]);

  return (
    <Modal
      centered
      title="Report Delay"
      width={800}
      open={open}
      className="UpdateDelayReportModal"
      onCancel={handleCancel}
      destroyOnClose
      maskClosable={false}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit} loading={loading} disabled={!canSubmit}>
          Submit
        </Button>,
      ]}
    >
      <div className="UpdateDelayReportModal__Body">
        <div className="UpdateDelayReportModal__Grid">
          <div className="UpdateDelayReportModal__Field">
            <label>Reason</label>
            <Select
              className="UpdateDelayReportModal__ReasonSelect"
              popupClassName="ReportDelayFilterSelect__Menu"
              options={groupedReasonOptions}
              value={delayReasonType || undefined}
              onChange={(v) => setDelayReasonType(v ?? "")}
              loading={reasonsLoading}
              disabled={reasonsLoading}
              allowClear
              showSearch
              filterOption={(inputValue, option) => option.label?.toLowerCase().includes(inputValue?.toLowerCase())}
              placeholder="Select a reason"
              notFoundContent="No delay reasons available"
            />
          </div>
          <div className="UpdateDelayReportModal__Field">
            <label>Expected date</label>
            <DatePicker
              className="UpdateDelayReportModal__DatePicker"
              showNow
              allowClear={false}
              format="MM/DD/YYYY"
              value={(expectedDate === "" || expectedDate === null) ? "" : dayjs(expectedDate)}
              onChange={(d) => setExpectedDate(d)}
              minDate={dayjs()} // today's date
            />
          </div>
        </div>

        <div className="UpdateDelayReportModal__Field">
          <label>{isCommentRequired ? "Comment" : "Comment (Optional)"}</label>
          <Input.TextArea
            value={comment}
            rows={5}
            placeholder="Enter comment"
            onChange={(e) => setComment(e.target.value)}
            maxLength={500}
          />
        </div>

        <ReportDelayTable
          rowKey="tests_in_sample_pk"
          dataSource={selectedRows || []}
          pagination
          columns={columns}
          loading={loading}
          scroll={{ x: "max-content", y: "300px" }}
          onRow={() => ({ style: { cursor: "default" } })}
        />
      </div>
    </Modal>
  );
}
